"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { Star } from "lucide-react";
import { useAnimationFrame, useReducedMotion } from "framer-motion";

import { formatDiscountPercentage } from "@/src/lib/utils/discount";

import WishlistToggleButton from "@/src/features/wishlist/components/WishlistToggleButton";

import type { HomeFeaturedProduct } from "../types";

const LOOP_COPIES = 4;
const START_COPY_INDEX = 1;
const AUTO_SCROLL_SPEED = 34;

export type RelatedProductItem = {
  product: HomeFeaturedProduct;
  href: string;
};

function formatPriceLabel(value: string) {
  const parsed = Number.parseFloat(value);

  if (Number.isNaN(parsed)) {
    return value;
  }

  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(parsed)} جنيه`;
}

export function RelatedProductMiniCard({ product, href }: RelatedProductItem) {
  return (
    <div className="group relative block w-[9.35rem] shrink-0 snap-start overflow-hidden rounded-[0.9rem] border border-[rgba(238,55,106,0.12)] bg-white shadow-[0_10px_24px_rgba(36,21,26,0.045)] transition hover:-translate-y-0.5 hover:border-[rgba(238,55,106,0.28)] sm:w-[10.25rem] md:w-[13.5rem] lg:w-[15rem]">
      <Link href={href} className="block">
        <div className="relative aspect-[1.35/1] overflow-hidden bg-[#fff5f8]">
          <Image
            src={product.image}
            alt={product.title}
            fill
            sizes="(min-width: 1024px) 240px, (min-width: 768px) 216px, (min-width: 640px) 164px, 150px"
            quality={100}
            className="object-cover transition duration-300 group-hover:scale-[1.035]"
          />
          {formatDiscountPercentage(product.discountPercentage) ? (
            <span
              dir="rtl"
              className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#ee376a] px-2 py-1 text-[10px] font-black text-white"
            >
              <span>{"\u062e\u0635\u0645"}</span>
              <bdi dir="ltr">
                {formatDiscountPercentage(product.discountPercentage)}
              </bdi>
            </span>
          ) : null}
        </div>

        <div className="space-y-1.5 p-2 text-right md:p-3">
          <h3 className="line-clamp-2 min-h-[2.1rem] text-[11px] font-black leading-[1.05rem] text-[var(--color-ink)] md:text-sm md:leading-6">
            {product.title}
          </h3>

          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-black text-[#ee376a] md:text-xs">
              {formatPriceLabel(product.price)}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[var(--color-ink-muted)]">
              {product.rating ? product.rating.toFixed(1) : "0.0"}
              <Star className="h-3 w-3 fill-current text-[#f5b301]" />
            </span>
          </div>
        </div>
      </Link>
      <div className="absolute left-2 top-2 flex flex-col gap-1.5">
        <WishlistToggleButton
          variantId={product.variantId}
          title={product.title}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-pink-200 bg-white text-[#ee376a] shadow-sm transition hover:bg-pink-50"
          filledClassName="!bg-[#ee376a] !text-white"
        />
      </div>
    </div>
  );
}

type RelatedProductsAutoCarouselProps = {
  products: RelatedProductItem[];
};

export default function RelatedProductsAutoCarousel({
  products,
}: RelatedProductsAutoCarouselProps) {
  const prefersReducedMotion = useReducedMotion();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const firstGroupRef = useRef<HTMLDivElement | null>(null);
  const sequenceWidthRef = useRef(0);
  const offsetRef = useRef(0);
  const isHoveredRef = useRef(false);
  const isFocusWithinRef = useRef(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    const firstGroup = firstGroupRef.current;

    if (!viewport || !firstGroup || products.length === 0) {
      return undefined;
    }

    const updateMetrics = () => {
      const sequenceWidth = firstGroup.offsetWidth;

      if (sequenceWidth <= 0) {
        return;
      }

      sequenceWidthRef.current = sequenceWidth;
      offsetRef.current = sequenceWidth * START_COPY_INDEX;
      viewport.scrollLeft = offsetRef.current;
    };

    updateMetrics();

    const resizeObserver = new ResizeObserver(() => {
      updateMetrics();
    });

    resizeObserver.observe(viewport);
    resizeObserver.observe(firstGroup);

    return () => {
      resizeObserver.disconnect();
    };
  }, [products]);

  useAnimationFrame((_, delta) => {
    if (
      prefersReducedMotion ||
      isHoveredRef.current ||
      isFocusWithinRef.current
    ) {
      return;
    }

    const viewport = viewportRef.current;
    const sequenceWidth = sequenceWidthRef.current;

    if (!viewport || sequenceWidth <= 0) {
      return;
    }

    const minimumOffset = sequenceWidth * START_COPY_INDEX;
    const maximumOffset = sequenceWidth * (START_COPY_INDEX + 1);
    const pixelsToMove = AUTO_SCROLL_SPEED * (delta / 1000);
    let nextOffset = offsetRef.current + pixelsToMove;

    if (nextOffset >= maximumOffset) {
      nextOffset -= sequenceWidth;
    }

    if (nextOffset < minimumOffset) {
      nextOffset = minimumOffset;
    }

    offsetRef.current = nextOffset;
    viewport.scrollLeft = nextOffset;
  });

  return (
    <div
      ref={viewportRef}
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
      onFocusCapture={() => {
        isFocusWithinRef.current = true;
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          isFocusWithinRef.current = false;
        }
      }}
      className="-mx-3 overflow-x-auto px-3 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:-mx-5 md:px-5 [&::-webkit-scrollbar]:hidden"
      style={{ direction: "ltr" }}
      data-reveal-group
    >
      <div className="flex w-max">
        {Array.from({ length: LOOP_COPIES }).map((_, copyIndex) => (
          <div
            key={copyIndex}
            ref={copyIndex === 0 ? firstGroupRef : undefined}
            className="flex flex-none gap-2.5 pe-2.5 md:gap-3.5 md:pe-3.5"
          >
            {products.map(({ product, href }, productIndex) => (
              <RelatedProductMiniCard
                key={`${copyIndex}-${product.variantId ?? product.sku}-${productIndex}`}
                product={product}
                href={href}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
